/**
 * Extracted Settings sections — small, focused pages rendered inside the
 * shell. Each section owns its own data fetch + save; the sticky Save bar is
 * driven by `useDirtyForm` from ../SettingsDirtyContext.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Copy, Trash2, Pencil, RefreshCcw, Upload, Loader2, Image as ImageIcon,
  Sparkles, Rocket, X, MoreHorizontal, ArrowUp, ArrowDown, Mail,
  Sliders, Eye, EyeOff, IndianRupee, CheckCircle2, Clock,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useClinicConfig } from "@/lib/clinicConfig";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { Time24Input } from "@/components/Time24Input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader as SheetHead, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SectionHeader, SettingCard } from "./SettingsLayout";
import { useDirtyForm } from "./SettingsDirtyContext";
import { findSection } from "./registry";
import { Link, Navigate } from "react-router-dom";
import { MODES, MODE_META } from "@/lib/workflowMode";
import { useClinic, primeClinicCache } from "@/lib/useClinic";

// ============================================================
// Placeholder route redirector.
// Sections marked `status: "hidden"` are filtered out of navigation +
// dashboard. If a user still lands on the old direct URL (bookmark, external
// link), we redirect them silently to the Settings home so they never see a
// placeholder — features are either working or not visible.
// ============================================================
export function ComingSoon() {
  return <Navigate to="/app/settings" replace />;
}

// ============================================================
// GENERAL → Clinic Profile
// ============================================================
export function ClinicProfileSection() {
  const { user, refresh } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clinic, setClinic] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/clinics/me");
    setClinic(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const form = useDirtyForm({
    initial: {
      name: clinic?.name || "", state: clinic?.state || "", address: clinic?.address || "",
      phone: clinic?.phone || "", email: clinic?.email || "", pan: clinic?.pan || "",
      tagline: clinic?.tagline || "",
      invoice_prefix: clinic?.invoice_prefix || "",
      google_place_id: clinic?.google_place_id || "",
      google_maps_url: clinic?.google_maps_url || "",
      google_rating: clinic?.google_rating ?? "",
      google_review_count: clinic?.google_review_count ?? "",
    },
    onSave: async (v) => {
      try {
        const clean = Object.fromEntries(Object.entries(v).filter(([, x]) => x !== undefined && x !== ""));
        // Coerce Google rating fields — the API validates 0-5 / ≥0.
        if ("google_rating" in clean)       clean.google_rating       = Number(clean.google_rating);
        if ("google_review_count" in clean) clean.google_review_count = Number(clean.google_review_count);
        await api.patch("/clinics/me", clean);
        toast.success("Clinic profile saved");
        await Promise.all([load(), refresh?.()]);
      } catch (err) {
        toast.error(apiError(err, "Could not save clinic profile"));
        throw err;
      }
    },
    deps: [clinic],
  });

  if (!clinic) return <Loader />;

  const set = (k) => (e) => form.setValue({ ...form.value, [k]: e.target.value });
  const inp = "w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm disabled:bg-linen focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10";
  const lbl = "block text-xs uppercase tracking-[0.12em] font-semibold text-ink-soft mb-1.5";
  return (
    <>
      <SectionHeader title="Clinic Profile"
                     description="Public details shown on invoices, the booking page, and outbound messages." />
      <SettingCard title="Identity">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={lbl} htmlFor="cp-name">Clinic name</label>
            <input id="cp-name" data-testid="cp-name" placeholder="Acme Dental" disabled={!isAdmin}
                   className={inp} value={form.value.name} onChange={set("name")} />
          </div>
          <div>
            <label className={lbl} htmlFor="cp-state">State</label>
            <input id="cp-state" data-testid="cp-state" placeholder="Maharashtra" disabled={!isAdmin}
                   className={inp} value={form.value.state} onChange={set("state")} />
          </div>
          <div className="md:col-span-2">
            <label className={lbl} htmlFor="cp-tagline">Tagline <span className="text-ink-soft normal-case tracking-normal">(optional — shown under the clinic name on invoices)</span></label>
            <input id="cp-tagline" data-testid="cp-tagline" placeholder="Gentle dentistry, lasting smiles" disabled={!isAdmin}
                   className={inp} value={form.value.tagline} onChange={set("tagline")} />
          </div>
          <div className="md:col-span-2">
            <label className={lbl} htmlFor="cp-address">Registered address</label>
            <input id="cp-address" data-testid="cp-address" placeholder="1 MG Road, Mumbai — 400001" disabled={!isAdmin}
                   className={inp} value={form.value.address} onChange={set("address")} />
          </div>
          <div>
            <label className={lbl} htmlFor="cp-phone">Phone</label>
            <input id="cp-phone" data-testid="cp-phone" placeholder="+91 98765 43210" disabled={!isAdmin}
                   className={inp} value={form.value.phone} onChange={set("phone")} />
          </div>
          <div>
            <label className={lbl} htmlFor="cp-email">Public email</label>
            <input id="cp-email" data-testid="cp-email" placeholder="care@yourclinic.in" disabled={!isAdmin}
                   className={inp} value={form.value.email} onChange={set("email")} type="email" />
          </div>
          <div>
            <label className={lbl} htmlFor="cp-pan">PAN</label>
            <input id="cp-pan" data-testid="cp-pan" placeholder="AABCU9603R" disabled={!isAdmin}
                   className={inp} value={form.value.pan} onChange={set("pan")} />
          </div>
          <div>
            <label className={lbl} htmlFor="cp-prefix">Invoice prefix</label>
            <input id="cp-prefix" data-testid="cp-prefix" placeholder="DAI" disabled={!isAdmin}
                   className={inp} value={form.value.invoice_prefix} onChange={set("invoice_prefix")} />
          </div>
        </div>
      </SettingCard>

      {/* Google Business — clinic-level rating shown on the booking page hero */}
      <SettingCard title="Google Business"
                    description="Rating shown on the public booking page hero. Only shown when rating and review count are both set.">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={lbl} htmlFor="cp-gplace">Google Place ID (optional)</label>
            <input id="cp-gplace" data-testid="cp-google-place-id"
                    placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4" disabled={!isAdmin}
                    className={inp} value={form.value.google_place_id} onChange={set("google_place_id")} />
          </div>
          <div>
            <label className={lbl} htmlFor="cp-gmaps">Google Maps listing URL</label>
            <input id="cp-gmaps" data-testid="cp-google-maps-url"
                    placeholder="https://maps.google.com/…" disabled={!isAdmin}
                    className={inp} value={form.value.google_maps_url} onChange={set("google_maps_url")} type="url" />
          </div>
          <div>
            <label className={lbl} htmlFor="cp-grating">Google rating (0–5)</label>
            <input id="cp-grating" data-testid="cp-google-rating"
                    placeholder="4.8" disabled={!isAdmin} type="number" min={0} max={5} step={0.1}
                    className={inp} value={form.value.google_rating} onChange={set("google_rating")} />
          </div>
          <div>
            <label className={lbl} htmlFor="cp-greview">Review count</label>
            <input id="cp-greview" data-testid="cp-google-review-count"
                    placeholder="326" disabled={!isAdmin} type="number" min={0} step={1}
                    className={inp} value={form.value.google_review_count} onChange={set("google_review_count")} />
          </div>
        </div>
      </SettingCard>
    </>
  );
}

// ============================================================
// GENERAL → Branding & Logo
// ============================================================
export function BrandingSection() {
  const { user, refresh: refreshUser } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clinic, setClinic] = useState(null);
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/clinics/me");
    setClinic(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const raw = clinic?.logo_url;
  // Resolve the logo URL: empty when no logo, absolute if user pasted a URL,
  // otherwise prefix with backend + cache-bust with logo_updated_at.
  let src = "";
  if (raw) {
    src = raw.startsWith("http")
      ? raw
      : `${backend}${raw}?t=${clinic?.logo_updated_at || ""}`;
  }

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) return toast.error("Please upload a PNG, JPG or WEBP image.");
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be under 2 MB.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/clinic/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated");
      await refreshUser?.();
      await load();
    } catch (err) { toast.error(apiError(err, "Could not upload logo")); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const remove = async () => {
    if (!window.confirm("Remove your clinic logo?")) return;
    try {
      await api.delete("/clinic/logo");
      toast.success("Logo removed");
      await refreshUser?.();
      await load();
    } catch (err) { toast.error(apiError(err, "Could not remove logo")); }
  };

  return (
    <>
      <SectionHeader title="Branding & Logo"
                     description="Your logo appears on invoices, the sidebar, and the public booking page." />
      <SettingCard dataTestId="settings-logo-block">
        <div className="flex items-center gap-5">
          <div className="w-24 h-24 rounded-2xl bg-linen border border-[#E8EAE9] grid place-items-center overflow-hidden shrink-0">
            {src ? <img src={src} alt="Clinic logo" className="w-full h-full object-contain" data-testid="settings-logo-preview" />
                 : <ImageIcon className="w-8 h-8 text-ink-disabled" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-ink">{raw ? "Your logo is live" : "No logo uploaded yet"}</div>
            <div className="mt-0.5 text-xs text-ink-mid max-w-lg">PNG / JPG / WEBP, under 2 MB. Square works best.</div>
            {isAdmin && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
                        data-testid="settings-logo-upload-btn"
                        className="inline-flex items-center gap-1.5 rounded-full bg-pine text-linen px-4 py-1.5 text-xs font-medium hover:opacity-90 disabled:opacity-60">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {uploading ? "Uploading…" : raw ? "Replace logo" : "Upload logo"}
                </button>
                {raw && (
                  <button type="button" onClick={remove} data-testid="settings-logo-remove-btn"
                          className="text-xs text-ink-mid hover:text-[#B94A48]">Remove</button>
                )}
                <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp"
                       onChange={(e) => handleFile(e.target.files?.[0])} className="hidden"
                       data-testid="settings-logo-input" />
              </div>
            )}
          </div>
        </div>
      </SettingCard>
    </>
  );
}

// ============================================================
// GENERAL → Working Hours (multi-shift, future-ready)
// ============================================================
const DAYS = [
  { key: "monday", label: "Monday" },      { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },{ key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },      { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

/**
 * Default day = two shifts (Morning + Evening), per spec. Every day starts
 * with this shape unless the clinic has saved something else. Legacy
 * single-window schedules (`{open, close, closed}`) are silently upgraded on
 * first render — the user just sees one Shift 1 populated with their old
 * values, ready to edit and save into the new schema.
 */
const DEFAULT_SHIFTS_WEEKDAY = [
  { open: "09:00", close: "13:00" },
  { open: "16:00", close: "20:00" },
];
const DEFAULT_SHIFTS_SUNDAY_CLOSED = { closed: true, shifts: [] };

// Stable per-row key used by React so removing shift #2 from [A, B, C] doesn't
// let React reuse the C row's DOM in B's slot (which caused stale time-input
// state before this change). Never sent to the server — stripped on save.
let _shiftKeySeed = 0;
const _newShiftKey = () => `sh_${++_shiftKeySeed}_${Date.now().toString(36)}`;
const _withKey = (s) => ({ ...s, _k: s._k || _newShiftKey() });

function _defaultDay(dayKey) {
  if (dayKey === "sunday") return { ...DEFAULT_SHIFTS_SUNDAY_CLOSED };
  return { closed: false, shifts: DEFAULT_SHIFTS_WEEKDAY.map(_withKey) };
}

/** Migrate a single day from any known schema into `{closed, shifts[]}`. */
function _migrateDay(dayKey, raw) {
  if (!raw) return _defaultDay(dayKey);
  if (Array.isArray(raw.shifts) && raw.shifts.length > 0) {
    return { closed: !!raw.closed, shifts: raw.shifts.map(_withKey) };
  }
  if (raw.open && raw.close) {
    return { closed: !!raw.closed, shifts: [_withKey({ open: raw.open, close: raw.close })] };
  }
  return { closed: !!raw.closed, shifts: raw.closed ? [] : DEFAULT_SHIFTS_WEEKDAY.map(_withKey) };
}

const DEFAULT_HOURS = Object.fromEntries(DAYS.map(({ key }) => [key, _defaultDay(key)]));

function _hhmmToMin(s) {
  if (!s || !s.includes(":")) return -1;
  const [h, m] = s.split(":"); return Number(h) * 60 + Number(m);
}

/** Validate one day. Returns an error string, or null if OK. */
function _validateDay(day) {
  if (day.closed) return null;
  const shifts = day.shifts || [];
  if (shifts.length === 0) return "Add at least one shift or mark the day as Closed.";
  for (let i = 0; i < shifts.length; i += 1) {
    const s = shifts[i];
    if (!s.open || !s.close) return `Shift ${i + 1}: fill both start and end.`;
    if (_hhmmToMin(s.close) <= _hhmmToMin(s.open)) return `Shift ${i + 1}: end must be after start.`;
  }
  // Overlap check on sorted shifts
  const sorted = [...shifts].map((s, i) => ({ ...s, i })).sort(
    (a, b) => _hhmmToMin(a.open) - _hhmmToMin(b.open)
  );
  for (let i = 1; i < sorted.length; i += 1) {
    if (_hhmmToMin(sorted[i].open) < _hhmmToMin(sorted[i - 1].close)) {
      return `Shifts ${sorted[i - 1].i + 1} and ${sorted[i].i + 1} overlap.`;
    }
  }
  return null;
}

export function WorkingHoursSection() {
  const { user } = useAuth();
  const cfg = useClinicConfig();
  const isAdmin = user?.role === "admin";
  const [clinic, setClinic] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/clinics/me");
    setClinic(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Build initial state migrating whatever schema the server returns.
  const initialValue = useMemo(() => {
    const saved = clinic?.working_hours || {};
    return Object.fromEntries(
      DAYS.map(({ key }) => [key, _migrateDay(key, saved[key])])
    );
  }, [clinic?.working_hours]);

  const form = useDirtyForm({
    initial: initialValue,
    onSave: async (v) => {
      // Validate every day before hitting the API — surface a friendly toast
      // on the first offending day.
      for (const { key, label } of DAYS) {
        const err = _validateDay(v[key]);
        if (err) { toast.error(`${label}: ${err}`); throw new Error(err); }
      }
      try {
        // Strip client-side stable-key markers before persisting.
        const payload = Object.fromEntries(
          Object.entries(v).map(([k, day]) => [
            k,
            day && Array.isArray(day.shifts)
              ? { ...day, shifts: day.shifts.map(({ _k, ...rest }) => rest) }
              : day,
          ])
        );
        await api.patch("/clinics/me", { working_hours: payload });
        toast.success("Working hours saved");
        load();
        // Broadcast to every open tab (Calendar, PublicBooking, Reception).
        cfg.invalidateAllTabs?.();
      } catch (err) {
        toast.error(apiError(err, "Could not save working hours"));
        throw err;
      }
    },
    deps: [clinic?.working_hours],
  });

  if (!clinic) return <Loader />;

  const updDay = (day, patch) =>
    form.setValue({ ...form.value, [day]: { ...form.value[day], ...patch } });

  const updShift = (day, idx, patch) => {
    const shifts = [...(form.value[day].shifts || [])];
    shifts[idx] = { ...shifts[idx], ...patch };
    updDay(day, { shifts });
  };

  const addShift = (day) => {
    const shifts = [...(form.value[day].shifts || [])];
    // Suggest a sensible slot right after the last shift ends.
    const last = shifts[shifts.length - 1];
    const nextOpen = last?.close || "09:00";
    let nextClose = "20:00";
    if (last?.close) {
      // If we can, offer a 3-hour slot after the last shift close.
      const [h, m] = last.close.split(":").map(Number);
      const nh = Math.min(h + 3, 23);
      nextClose = `${String(nh).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      if (_hhmmToMin(nextClose) <= _hhmmToMin(nextOpen)) nextClose = "20:00";
    }
    shifts.push(_withKey({ open: nextOpen, close: nextClose }));
    updDay(day, { shifts });
  };

  const removeShift = (day, idx) => {
    const shifts = [...(form.value[day].shifts || [])];
    if (shifts.length <= 1) return; // Shift 1 cannot be deleted if it's the only one.
    shifts.splice(idx, 1);
    updDay(day, { shifts });
  };

  return (
    <>
      <SectionHeader title="Working Hours"
                     description="Multiple daily shifts (e.g. morning + evening) are supported. The Calendar and Online Booking will only allow slots inside these shifts." />
      <SettingCard>
        <div className="divide-y divide-[#F0EBDD]">
          {DAYS.map(({ key, label }) => {
            const day = form.value[key] || _defaultDay(key);
            const shifts = day.shifts || [];
            return (
              <div
                key={key}
                className="py-3 first:pt-0 last:pb-0 grid grid-cols-[110px_1fr_auto] gap-4 items-start"
                data-testid={`wh-day-${key}`}
              >
                <div className="font-medium text-ink pt-2">{label}</div>

                <div className={`space-y-2 ${day.closed ? "opacity-40 pointer-events-none" : ""}`}>
                  {!day.closed && shifts.map((s, idx) => (
                    <div key={s._k || `sh-fallback-${idx}`} className="flex items-center gap-2" data-testid={`wh-${key}-shift-${idx}`}>
                      <div className="text-[10px] uppercase tracking-widest text-ink-soft w-20 shrink-0">
                        Shift {idx + 1}
                      </div>
                      <Time24Input
                        disabled={!isAdmin} value={s.open || ""}
                        onChange={(v) => updShift(key, idx, { open: v })}
                        className="rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm bg-white w-[84px] text-center"
                        data-testid={`wh-${key}-shift-${idx}-open`}
                      />
                      <span className="text-ink-soft text-xs">→</span>
                      <Time24Input
                        disabled={!isAdmin} value={s.close || ""}
                        onChange={(v) => updShift(key, idx, { close: v })}
                        className="rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm bg-white w-[84px] text-center"
                        data-testid={`wh-${key}-shift-${idx}-close`}
                      />
                      <button
                        type="button"
                        onClick={() => removeShift(key, idx)}
                        disabled={!isAdmin || shifts.length <= 1}
                        title={shifts.length <= 1 ? "Add another shift first, or mark the day Closed." : "Remove shift"}
                        data-testid={`wh-${key}-shift-${idx}-remove`}
                        className="w-8 h-8 rounded-md text-ink-soft hover:text-red-600 hover:bg-red-50 grid place-items-center disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {!day.closed && (
                    <button
                      type="button"
                      onClick={() => addShift(key)}
                      disabled={!isAdmin}
                      data-testid={`wh-${key}-add-shift`}
                      className="text-[11px] px-3 py-1.5 rounded-full border border-pine/25 text-pine bg-white hover:bg-pine/5 inline-flex items-center gap-1.5 disabled:opacity-40"
                    >
                      <Plus className="w-3 h-3" /> Add shift
                    </button>
                  )}
                </div>

                <label className="text-xs text-ink-mid flex items-center gap-2 pt-2">
                  <input
                    type="checkbox" disabled={!isAdmin} className="accent-pine"
                    checked={!!day.closed}
                    onChange={(e) => updDay(key, { closed: e.target.checked })}
                    data-testid={`wh-${key}-closed`}
                  />
                  Closed
                </label>
              </div>
            );
          })}
        </div>
      </SettingCard>
    </>
  );
}


// ============================================================
// BILLING → Billing Preferences (Per Visit / Upfront / By Stage / Manual)
// ============================================================
const BILLING_MODES = [
  {
    id: "per_visit",
    title: "Bill per visit",
    subtitle: "Default. Bill only what was completed today.",
    body: "Best for most clinics. When you finish today's session, only that session's completed treatments show as 'ready to bill'. Future sessions stay hidden until they're done.",
    example: "RCT Session 1 → billable now. Session 2 & 3 → not yet.",
  },
  {
    id: "upfront",
    title: "Bill entire treatment upfront",
    subtitle: "Charge for the whole plan at the first visit.",
    body: "Ideal for orthodontics and large treatment plans. Both completed AND scheduled treatments appear as 'ready to bill' so you can invoice the whole plan on day one.",
    example: "All 3 RCT sessions → billable together on day one.",
  },
  {
    id: "by_stage",
    title: "Bill by stage",
    subtitle: "Group by milestone — bill each stage as it completes.",
    body: "Same visible list as 'Per visit' today. Multi-stage roadmap surfaces here in Phase 3 when the Treatment Planner exposes explicit stages.",
    example: "Cleaning + polishing → billed at stage 1 close-out.",
    tag: "phase 3",
  },
  {
    id: "manual",
    title: "Manual only",
    subtitle: "Never auto-suggest — the receptionist adds every line.",
    body: "The wizard skips the 'ready to bill' card entirely. Use this only if your practice already has an external workflow you don't want us touching.",
  },
];

export function BillingPreferencesSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clinic, setClinic] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/clinics/me");
    setClinic(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const form = useDirtyForm({
    initial: {
      mode: clinic?.billing_preferences?.mode || "per_visit",
      auto_open_on_complete: !!clinic?.billing_preferences?.auto_open_on_complete,
    },
    onSave: async (v) => {
      try {
        await api.patch("/clinics/me", { billing_preferences: v });
        toast.success("Billing preferences saved");
        load();
      } catch (err) {
        toast.error(apiError(err, "Could not save billing preferences"));
        throw err;
      }
    },
    deps: [clinic],
  });
  if (!clinic) return <Loader />;

  return (
    <>
      <SectionHeader
        title="Billing Preferences"
        description="How the New Invoice wizard should decide what to bill. This affects every receptionist across the clinic."
      />

      <SettingCard title="Default billing mode"
                   description="Pick the model that matches how your practice invoices patients today.">
        <div className="grid md:grid-cols-2 gap-4" data-testid="billing-modes">
          {BILLING_MODES.map((m) => {
            const active = form.value.mode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                disabled={!isAdmin}
                onClick={() => form.setValue({ ...form.value, mode: m.id })}
                data-testid={`billing-mode-${m.id}`}
                className={`text-left p-5 rounded-2xl border-2 transition ${
                  active
                    ? "border-pine bg-pine/5 shadow-[0_10px_30px_-20px_rgba(245,83,45,0.4)]"
                    : "border-[#EFEADC] bg-white hover:border-ink-faint"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={`w-4 h-4 mt-1 shrink-0 rounded-full border-2 grid place-items-center ${
                    active ? "border-pine" : "border-ink-faint"
                  }`}>
                    {active && <span className="w-2 h-2 rounded-full bg-pine" />}
                  </div>
                  {m.tag && (
                    <span className="text-[9px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-full bg-[#F5E0D2] text-[#B0521F]">
                      {m.tag}
                    </span>
                  )}
                </div>
                <div className="mt-2 text-[15px] font-bold text-ink leading-snug">{m.title}</div>
                <div className="text-xs text-ink-mid mt-1 font-medium">{m.subtitle}</div>
                <div className="text-xs text-ink-mid mt-3 leading-relaxed">{m.body}</div>
                {m.example && (
                  <div className="text-[11px] text-pine mt-3 font-semibold italic">{m.example}</div>
                )}
              </button>
            );
          })}
        </div>
      </SettingCard>

      <SettingCard title="Chairside shortcut"
                   description="When a doctor marks a treatment complete, offer to open the New Invoice wizard right there.">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="billing-auto-open">
          <input type="checkbox" className="mt-1 accent-pine"
                 disabled={!isAdmin}
                 checked={form.value.auto_open_on_complete}
                 onChange={(e) => form.setValue({ ...form.value, auto_open_on_complete: e.target.checked })} />
          <div className="flex-1">
            <div className="text-sm font-semibold text-ink">Show "Bill this visit" on treatment complete</div>
            <div className="text-xs text-ink-mid mt-0.5">
              Adds a subtle CTA to the completed-treatment toast in Chairside so the receptionist can invoice in a single click. Toggle off if your workflow bills at reception instead.
            </div>
          </div>
        </label>
      </SettingCard>
    </>
  );
}



// ============================================================
// BILLING → Invoice Settings (GST vs Non-GST)
// ============================================================
export function InvoiceSettingsSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clinic, setClinic] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/clinics/me");
    setClinic(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!clinic) return <Loader />;

  const activeType = clinic.invoice_type || (clinic.gstin ? "gst" : "non_gst");
  const options = [
    { id: "gst",     title: "GST Invoice",     desc: "Tax invoice with GSTIN, HSN, CGST/SGST breakdown." },
    { id: "non_gst", title: "Non-GST Invoice", desc: "Clean invoice without tax lines or GSTIN." },
  ];
  return (
    <>
      <SectionHeader title="Invoice Settings"
                     description="Switch between GST-compliant tax invoices and non-GST invoices. Applies to new invoices only." />
      <SettingCard>
        <div className="grid md:grid-cols-2 gap-3">
          {options.map((opt) => {
            const active = activeType === opt.id;
            const disabled = !isAdmin || (opt.id === "gst" && !clinic.gstin);
            return (
              <button key={opt.id} type="button"
                      data-testid={`invoice-type-${opt.id}`}
                      disabled={disabled}
                      onClick={async () => {
                        try {
                          await api.patch("/clinics/me", { invoice_type: opt.id });
                          toast.success(`Switched to ${opt.title}`);
                          load();
                        } catch (err) { toast.error(apiError(err, "Could not update")); }
                      }}
                      className={`text-left p-4 rounded-2xl border-2 transition-all ${
                        active ? "border-pine bg-linen" : "border-[#E8EAE9] hover:border-pine/40"
                      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display text-base text-ink">{opt.title}</div>
                  {active && <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest bg-pine text-linen">Active</span>}
                </div>
                <div className="text-xs text-ink-mid mt-1">{opt.desc}</div>
                {opt.id === "gst" && !clinic.gstin && (
                  <div className="text-[11px] text-[#B0521F] mt-2">Add a GSTIN under GST & Tax first.</div>
                )}
              </button>
            );
          })}
        </div>
      </SettingCard>
    </>
  );
}

// ============================================================
// BILLING → Invoice Template
// ============================================================
export function InvoiceTemplateSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clinic, setClinic] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/clinics/me");
    setClinic(data);
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!clinic) return <Loader />;

  const toggles = [
    { key: "show_account_summary",      label: "Show Patient Account Summary", hint: "Master switch." },
    { key: "show_previous_outstanding", label: "Show Previous Outstanding",     hint: "Amount owed before this invoice." },
    { key: "show_current_outstanding",  label: "Show Current Outstanding",      hint: "Highlighted in an amber accent box." },
    { key: "show_wallet_usage",         label: "Show Wallet Usage",             hint: "Only rendered when wallet was used." },
    { key: "show_payment_status",       label: "Show Payment Status",           hint: "Fully-paid / partial / unpaid badge." },
  ];
  const tpl = clinic.invoice_template || {};

  return (
    <>
      <SectionHeader title="Invoice Template"
                     description="The invoice PDF now shows service line items only. The toggles below control the Patient Account Summary shown in WhatsApp/Email payment messages." />
      <SettingCard title="Account Summary (WhatsApp / Email messages)"
                   description="Applies to the account-summary block in outbound WhatsApp & Email payment messages — not the invoice PDF.">
        <div className="grid md:grid-cols-2 gap-3">
          {toggles.map((opt) => {
            const checked = tpl[opt.key] !== false;
            return (
              <label key={opt.key}
                     data-testid={`tpl-${opt.key.replace(/_/g, "-")}`}
                     className={`flex items-start gap-3 p-3 rounded-xl border ${
                       checked ? "border-pine/40 bg-linen" : "border-[#E8EAE9] hover:border-pine/30"
                     } cursor-pointer`}>
                <input type="checkbox" className="mt-1 accent-pine" disabled={!isAdmin}
                       checked={checked}
                       onChange={async (e) => {
                         try {
                           await api.patch("/clinics/me", { invoice_template: { [opt.key]: e.target.checked } });
                           load();
                         } catch (err) { toast.error(apiError(err, "Save failed")); }
                       }} />
                <div>
                  <div className="text-sm font-medium text-ink">{opt.label}</div>
                  <div className="text-xs text-ink-mid mt-0.5">{opt.hint}</div>
                </div>
              </label>
            );
          })}
        </div>
      </SettingCard>

      <SettingCard title="Invoice Notes & Signatory"
                   description="Notes print at the bottom of every invoice. Leave blank to use the default two lines.">
        <InvoiceNotesEditor tpl={tpl} isAdmin={isAdmin} onSaved={load} />
      </SettingCard>
    </>
  );
}

function InvoiceNotesEditor({ tpl, isAdmin, onSaved }) {
  const initialNotes = Array.isArray(tpl.notes) ? tpl.notes.join("\n") : (tpl.notes || "");
  const [notes, setNotes] = useState(initialNotes);
  const [signatory, setSignatory] = useState(tpl.signatory_name || "");
  const [saving, setSaving] = useState(false);
  const inp = "w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm disabled:bg-linen focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10";
  const lbl = "block text-xs uppercase tracking-[0.12em] font-semibold text-ink-soft mb-1.5";
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/clinics/me", {
        invoice_template: {
          notes: notes.split("\n").map((l) => l.trim()).filter(Boolean),
          signatory_name: signatory.trim(),
        },
      });
      toast.success("Invoice notes saved");
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4">
      <div>
        <label className={lbl} htmlFor="tpl-notes">Notes (one per line)</label>
        <textarea id="tpl-notes" data-testid="tpl-notes" rows={3} disabled={!isAdmin}
                  className={`${inp} resize-none`} value={notes}
                  placeholder={"Please make the payment on or before the invoice date.\nFor any queries, please contact the clinic."}
                  onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div>
        <label className={lbl} htmlFor="tpl-signatory">Signatory name <span className="text-ink-soft normal-case tracking-normal">(optional — shown above “Authorised Signatory”)</span></label>
        <input id="tpl-signatory" data-testid="tpl-signatory" disabled={!isAdmin}
               className={inp} value={signatory} placeholder="Dr. A. Sharma"
               onChange={(e) => setSignatory(e.target.value)} />
      </div>
      {isAdmin && (
        <button data-testid="tpl-notes-save" onClick={save} disabled={saving}
                className="btn-primary text-sm px-5 py-2 disabled:opacity-60">
          {saving ? "Saving…" : "Save notes"}
        </button>
      )}
    </div>
  );
}
export function GstTaxSection() {
  const { user, refresh } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clinic, setClinic] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/clinics/me");
    setClinic(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const form = useDirtyForm({
    initial: { gstin: clinic?.gstin || "", state: clinic?.state || "" },
    onSave: async (v) => {
      try {
        await api.patch("/clinics/me", v);
        toast.success("GST details saved");
        await Promise.all([load(), refresh?.()]);
      } catch (err) {
        toast.error(apiError(err, "Could not save GST details"));
        throw err;
      }
    },
    deps: [clinic],
  });
  if (!clinic) return <Loader />;

  const inp = "w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm disabled:bg-linen focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10";
  const lbl = "block text-xs uppercase tracking-[0.12em] font-semibold text-ink-soft mb-1.5";
  return (
    <>
      <SectionHeader title="GST & Tax"
                     description="Registered GSTIN and clinic state. Used to compute CGST/SGST vs IGST on tax invoices." />
      <SettingCard>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={lbl} htmlFor="gst-gstin">GSTIN</label>
            <input id="gst-gstin" placeholder="27AABCU9603R1ZM" disabled={!isAdmin}
                   data-testid="gst-gstin" className={inp}
                   value={form.value.gstin} onChange={(e) => form.setValue({ ...form.value, gstin: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <label className={lbl} htmlFor="gst-state">State</label>
            <input id="gst-state" placeholder="Maharashtra" disabled={!isAdmin}
                   data-testid="gst-state" className={inp}
                   value={form.value.state} onChange={(e) => form.setValue({ ...form.value, state: e.target.value })} />
          </div>
        </div>
      </SettingCard>
    </>
  );
}

// ============================================================
// CLINICAL → Procedure Catalogue
// ============================================================
const PROC_CATS = ["Preventive", "Restorative", "Endodontic", "Prosthetic", "Surgical", "Cosmetic", "Other"];

export function ProceduresSection() {
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", variant: "", category: "Other", cost: 0, duration_min: 30, notes: "", show_pricing_online: null });

  const load = useCallback(async () => {
    const { data } = await api.get("/procedure-catalog");
    setCatalog(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", variant: "", category: "Other", cost: 0, duration_min: 30, notes: "", show_pricing_online: null });
    setOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "", variant: item.variant || "", category: item.category || "Other",
      cost: item.cost || 0, duration_min: item.duration_min || 30, notes: item.notes || "",
      show_pricing_online: item.show_pricing_online ?? null,
    });
    setOpen(true);
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name.trim(), variant: form.variant.trim() || null,
        category: form.category, cost: Number(form.cost),
        duration_min: Number(form.duration_min) || null, notes: form.notes || null,
        active: true, show_pricing_online: form.show_pricing_online,
      };
      if (editing) await api.patch(`/procedure-catalog/${editing.id}`, payload);
      else await api.post("/procedure-catalog", payload);
      toast.success(editing ? "Procedure updated" : "Procedure added");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this procedure?")) return;
    try { await api.delete(`/procedure-catalog/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(apiError(err, "Failed")); }
  };
  const reset = async () => {
    if (!window.confirm("Replace ALL procedures with the default price list?")) return;
    try {
      const { data } = await api.post("/procedure-catalog/reset-defaults", {});
      toast.success(`Reset · ${data.seeded} defaults loaded`); load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  const byCat = useMemo(() => {
    const g = {};
    for (const c of PROC_CATS) g[c] = [];
    for (const it of catalog) (g[it.category] || g.Other).push(it);
    return g;
  }, [catalog]);

  const inp = "w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm";
  return (
    <>
      <SectionHeader title="Procedure Catalogue"
                     description="Master list of treatments, prices, and default durations used everywhere in Viyora." />
      <SettingCard>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="text-xs text-ink-mid">{catalog.length} procedures</div>
          <div className="flex items-center gap-2">
            <button data-testid="btn-reset-catalog" onClick={reset}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-pine/20 text-ink hover:bg-linen">
              <RefreshCcw className="w-3 h-3" /> Reset defaults
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="btn-add-procedure" onClick={openNew}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-pine text-linen">
                  <Plus className="w-3 h-3" /> Add procedure
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit procedure" : "Add procedure"}</DialogTitle></DialogHeader>
                <form onSubmit={save} className="space-y-3" data-testid="catalog-form">
                  <div className="grid grid-cols-2 gap-2">
                    <input required data-testid="cat-name" placeholder="Name" className={inp}
                           value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <input data-testid="cat-variant" placeholder="Variant" className={inp}
                           value={form.variant} onChange={(e) => setForm({ ...form, variant: e.target.value })} />
                  </div>
                  <select data-testid="cat-category" className={inp}
                          value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {PROC_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <input required data-testid="cat-cost" type="number" min={0} placeholder="Cost" className={inp}
                           value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
                    <input data-testid="cat-duration" type="number" min={0} placeholder="Duration (min)" className={inp}
                           value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} />
                  </div>
                  <textarea data-testid="cat-notes" placeholder="Notes" rows={2} className={`${inp} resize-none`}
                            value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  <div className="rounded-xl border border-[#EFEADC] bg-[#FBFAF6] p-3 text-xs">
                    <div className="font-medium text-ink mb-1">Pricing on public booking page</div>
                    <div className="flex gap-2 flex-wrap">
                      {[
                        { v: null,  label: "Follow clinic default" },
                        { v: true,  label: "Always show fee" },
                        { v: false, label: "Always hide fee" },
                      ].map((opt) => (
                        <button key={String(opt.v)} type="button"
                                data-testid={`cat-vis-${opt.v === null ? "inherit" : opt.v ? "show" : "hide"}`}
                                onClick={() => setForm({ ...form, show_pricing_online: opt.v })}
                                className={`px-3 py-1.5 rounded-full border text-xs ${
                                  form.show_pricing_online === opt.v
                                    ? "bg-pine text-linen border-pine"
                                    : "bg-white text-ink border-[#E8EAE9] hover:border-pine"
                                }`}>{opt.label}</button>
                      ))}
                    </div>
                  </div>
                  <button data-testid="cat-submit" className="w-full btn-primary text-sm">Save</button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {PROC_CATS.map((cat) => {
          const items = byCat[cat] || [];
          if (!items.length) return null;
          return (
            <div key={cat} className="mb-5">
              <div className="overline text-ink-soft mb-2">{cat}</div>
              <div className="rounded-xl overflow-hidden border border-[#E8EAE9]">
                <table className="w-full text-sm">
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} data-testid={`catalog-row-${it.id}`} className="border-t border-[#E8EAE9] first:border-t-0">
                        <td className="px-4 py-2.5 font-medium text-ink">{it.name}</td>
                        <td className="px-4 py-2.5 text-ink-mid">{it.variant || "—"}</td>
                        <td className="px-4 py-2.5 text-right font-mono">
                          ₹{Number(it.cost).toLocaleString("en-IN")}
                          {it.show_pricing_online === false && (
                            <span className="ml-2 inline-block text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#F5E0D2] text-[#B0521F]">hidden</span>
                          )}
                          {it.show_pricing_online === true && (
                            <span className="ml-2 inline-block text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#E8F0EB] text-[#2D6A4F]">shown</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right w-20">
                          <button onClick={() => openEdit(it)} className="text-ink-mid hover:text-ink mr-2"><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => remove(it.id)} className="text-[#B94A48] hover:text-[#8A2E2C]"><Trash2 className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </SettingCard>
    </>
  );
}

// ============================================================
// DOCTORS & STAFF — shared Doctors + Staff (team list)
// Redesigned: card/list layout with per-doctor Booking Settings drawer.
// UI-only refactor — backend endpoints are unchanged.
// ============================================================

/** Two-letter initials from a person's name — used as avatar fallback. */
function _initials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Deterministic avatar tint per user id so cards feel personal without images. */
function _avatarTint(seed = "") {
  const palette = [
    "bg-[#F5E0D2] text-[#B0521F]",
    "bg-[#E8F0EB] text-[#2D6A4F]",
    "bg-[#E4E7F5] text-[#3C4B99]",
    "bg-[#F5E4EE] text-[#8E2A5A]",
    "bg-[#EFEAD8] text-[#7A5A11]",
    "bg-[#DDEBF4] text-[#1E5F84]",
  ];
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

const SCOPE_LABEL = {
  every_visit:  "Every visit",
  first_visit:  "First visit only",
  consult_only: "Consult visits only",
  free:         "Free / complimentary",
};

const ROLE_LABEL = {
  doctor: "Doctor", admin: "Admin", receptionist: "Receptionist",
};

/**
 * Right-side drawer that hosts every "Online Booking" setting for a single
 * doctor. Auto-saves on change via the same PATCH /team/:id endpoint that the
 * old table relied on — no backend contract changed. `member` is kept live via
 * the parent's team list so toggles reflect server state after each write.
 */
// Circular photo uploader used inside the DoctorBookingSheet. Reads a
// file from the admin's disk, POSTs to /team/{uid}/picture, refreshes
// the parent via onPatch so the new URL propagates without a page
// reload. Delete clears the field. No pod-local persistence — the
// backend hands off to Emergent object storage.
function PhotoUploader({ member, onPatch }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Photo must be ≤ 6 MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/team/${member.id}/picture`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Merge every field the server returned so rating/exp/etc. stay
      // in sync too (the endpoint returns the full row).
      onPatch(member.id, { picture: data.picture });
      toast.success("Photo updated");
    } catch (err) {
      toast.error(apiError(err, "Photo upload failed"));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm("Remove the current photo?")) return;
    setBusy(true);
    try {
      await api.delete(`/team/${member.id}/picture`);
      onPatch(member.id, { picture: null });
      toast.success("Photo removed");
    } catch (err) {
      toast.error(apiError(err, "Remove failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <label className="block text-xs font-medium text-ink-mid mb-2">
        Profile photo
      </label>
      <div className="flex items-center gap-4">
        <div
          className="w-16 h-16 rounded-full overflow-hidden bg-linen border border-[#EFEADC] shrink-0 grid place-items-center"
          data-testid={`sheet-photo-${member.id}`}
        >
          {member.picture ? (
            <img src={member.picture} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className={`w-full h-full grid place-items-center text-sm font-semibold ${_avatarTint(member.id)}`}>
              {_initials(member.name)}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid={`sheet-photo-input-${member.id}`}
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              data-testid={`sheet-photo-upload-${member.id}`}
              className="text-xs px-3 py-1.5 rounded-full border border-pine/30 text-pine hover:bg-pine/5 disabled:opacity-50"
            >
              {member.picture ? "Change photo" : "Upload photo"}
            </button>
            {member.picture && (
              <button
                type="button"
                disabled={busy}
                onClick={remove}
                data-testid={`sheet-photo-remove-${member.id}`}
                className="text-xs px-3 py-1.5 rounded-full text-[#B94A48] hover:bg-[#F9E5E3] disabled:opacity-50"
              >
                Remove
              </button>
            )}
          </div>
          <div className="text-[11px] text-ink-soft mt-1.5">
            JPG, PNG or WebP. Recommended square ≥ 500×500 px.
          </div>
        </div>
      </div>
    </div>
  );
}


function DoctorBookingSheet({ member, open, onOpenChange, onPatch }) {
  if (!member) return null;
  const online = member.available_for_online_booking !== false;
  const feeAuto = member.consultation_fee_apply_auto !== false;
  const feeVisible = member.consultation_fee_visible_online !== false;
  const pricing = member.show_pricing_online !== false;
  const scope = member.consultation_fee_charge_scope || "every_visit";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto p-0"
        data-testid={`doctor-booking-sheet-${member.id}`}
      >
        <div className="border-b border-[#EFEADC] px-6 py-5 flex items-center gap-3">
          <div className={`w-11 h-11 rounded-full grid place-items-center font-semibold text-sm ${_avatarTint(member.id)}`}>
            {_initials(member.name)}
          </div>
          <div className="min-w-0">
            <SheetHead className="p-0 text-left">
              <SheetTitle className="font-display text-xl text-ink truncate">
                {member.name || "Doctor"}
              </SheetTitle>
              <SheetDescription className="text-xs text-ink-mid truncate">
                {member.specialty || "Booking settings"}
              </SheetDescription>
            </SheetHead>
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Availability -------------------------------------------------- */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-soft mb-3">
              Availability
            </div>
            <label className="flex items-start justify-between gap-4 p-4 rounded-xl border border-[#EFEADC] cursor-pointer hover:border-pine/40 transition"
                   data-testid={`sheet-online-toggle-${member.id}`}>
              <div>
                <div className="text-sm font-medium text-ink">Show on public booking page</div>
                <div className="text-xs text-ink-mid mt-0.5">
                  When off, this doctor is hidden from patients and their slots are blocked.
                </div>
              </div>
              <input
                type="checkbox" className="accent-pine mt-1 h-4 w-4"
                checked={online}
                onChange={(e) => onPatch(member.id, { available_for_online_booking: e.target.checked })}
              />
            </label>
          </section>

          {/* Public profile — powers the doctor card on /book/{slug} */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-soft mb-3">
              Doctor profile for booking page
            </div>
            <div className="rounded-xl border border-[#EFEADC] p-4 space-y-4">
              <PhotoUploader member={member} onPatch={onPatch} />
              <div>
                <label className="block text-xs font-medium text-ink-mid mb-1.5">
                  Qualification / Degrees
                </label>
                <input
                  type="text"
                  defaultValue={member.qualification || ""}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (member.qualification || "")) {
                      onPatch(member.id, { qualification: v || null });
                    }
                  }}
                  placeholder="BDS, MDS – Endodontist"
                  data-testid={`sheet-qualification-${member.id}`}
                  className="w-full rounded-lg border border-[#E8EAE9] px-3 py-2 text-sm focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-mid mb-1.5">
                  Years of experience
                </label>
                <input
                  type="number" min={0} step={1}
                  defaultValue={member.years_experience ?? ""}
                  onBlur={(e) => {
                    const raw = e.target.value.trim();
                    const v = raw === "" ? null : Number(raw);
                    if (v !== (member.years_experience ?? null)) {
                      onPatch(member.id, { years_experience: v });
                    }
                  }}
                  placeholder="e.g. 8"
                  data-testid={`sheet-experience-${member.id}`}
                  className="w-full rounded-lg border border-[#E8EAE9] px-3 py-2 text-sm focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10"
                />
                <div className="text-[11px] text-ink-soft mt-1.5">
                  Rendered as "12+ years experience" on the doctor card.
                </div>
              </div>
              <div className="pt-3 border-t border-[#F1EBDD]">
                <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">
                  Google Business — clinic-wide (see Clinic Profile)
                </div>
                <div className="text-[11px] text-ink-soft leading-relaxed">
                  Google rating is now managed at the clinic level and shown
                  in the booking-page hero. Set it under Settings → Clinic
                  Profile → Google Business.
                </div>
              </div>
            </div>
          </section>

          {/* Consultation Fee --------------------------------------------- */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-soft mb-3">
              Consultation Fee
            </div>
            <div className="rounded-xl border border-[#EFEADC] p-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-ink-mid mb-1.5">
                  Amount
                </label>
                <div className="relative">
                  <IndianRupee className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
                  <input
                    type="number" min={0} step={50}
                    value={member.consultation_fee ?? ""}
                    onChange={(e) => onPatch(member.id, {
                      consultation_fee: e.target.value === "" ? null : Number(e.target.value),
                    })}
                    placeholder="0"
                    data-testid={`sheet-consult-fee-${member.id}`}
                    className="w-full rounded-lg border border-[#E8EAE9] pl-8 pr-3 py-2 text-sm focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10"
                  />
                </div>
                <div className="text-[11px] text-ink-soft mt-1.5">
                  Leave blank for free / pay-at-clinic.
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-ink-mid mb-1.5">
                  When to charge
                </label>
                <select
                  value={scope}
                  onChange={(e) => onPatch(member.id, { consultation_fee_charge_scope: e.target.value })}
                  data-testid={`sheet-consult-scope-${member.id}`}
                  className="w-full rounded-lg border border-[#E8EAE9] px-3 py-2 text-sm bg-white focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10"
                >
                  <option value="every_visit">Every visit</option>
                  <option value="first_visit">First visit only</option>
                  <option value="consult_only">Consult visits only</option>
                  <option value="free">Free / complimentary</option>
                </select>
              </div>

              <label className="flex items-start justify-between gap-3 cursor-pointer">
                <div>
                  <div className="text-sm text-ink">Auto-add fee to appointments</div>
                  <div className="text-[11px] text-ink-mid mt-0.5">
                    Reception can still override at billing.
                  </div>
                </div>
                <input
                  type="checkbox" className="accent-pine mt-1 h-4 w-4"
                  checked={feeAuto}
                  onChange={(e) => onPatch(member.id, { consultation_fee_apply_auto: e.target.checked })}
                  data-testid={`sheet-consult-auto-${member.id}`}
                />
              </label>

              <label className="flex items-start justify-between gap-3 cursor-pointer">
                <div>
                  <div className="text-sm text-ink">Show fee on public booking</div>
                  <div className="text-[11px] text-ink-mid mt-0.5">
                    Patients see the fee before confirming.
                  </div>
                </div>
                <input
                  type="checkbox" className="accent-pine mt-1 h-4 w-4"
                  checked={feeVisible}
                  onChange={(e) => onPatch(member.id, { consultation_fee_visible_online: e.target.checked })}
                  data-testid={`sheet-consult-visible-${member.id}`}
                />
              </label>
            </div>
          </section>

          {/* Pricing visibility ------------------------------------------- */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-soft mb-3">
              Procedure Pricing
            </div>
            <label className="flex items-start justify-between gap-4 p-4 rounded-xl border border-[#EFEADC] cursor-pointer hover:border-pine/40 transition"
                   data-testid={`sheet-pricing-toggle-${member.id}`}>
              <div>
                <div className="text-sm font-medium text-ink">Show procedure prices on booking page</div>
                <div className="text-xs text-ink-mid mt-0.5">
                  Hide to keep pricing private; patients still see the consultation fee if enabled above.
                </div>
              </div>
              <input
                type="checkbox" className="accent-pine mt-1 h-4 w-4"
                checked={pricing}
                onChange={(e) => onPatch(member.id, { show_pricing_online: e.target.checked })}
              />
            </label>
          </section>

          <div className="rounded-xl bg-linen border border-[#EFEADC] px-4 py-3 flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-pine mt-0.5 shrink-0" />
            <div className="text-[11px] text-ink-mid leading-relaxed">
              Changes are saved automatically as you edit. Close this panel anytime.
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function TeamSection({ filter = "all" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [team, setTeam] = useState([]);
  const [inactive, setInactive] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "receptionist" });
  const [sheetDoctorId, setSheetDoctorId] = useState(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/team");
    setTeam(data);
    if (isAdmin) {
      // Only pending invites — the backend now filters `used: false` by
      // default. We also fetch the inactive team roster so admins can
      // one-click reactivate a previously removed doctor.
      const [inv, inact] = await Promise.all([
        api.get("/invites"),
        api.get("/team", { params: { include_inactive: true } }),
      ]);
      setInvites(inv.data);
      setInactive((inact.data || []).filter((m) => m.is_active === false));
    }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invites", inviteForm);
      toast.success("Invite created");
      setInviteOpen(false); setInviteForm({ email: "", role: "receptionist" });
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };
  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/signup?invite=${token}`);
    toast.success("Invite link copied");
  };
  const copyEmail = (email) => {
    navigator.clipboard.writeText(email || "");
    toast.success("Email copied");
  };
  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite?")) return;
    await api.delete(`/invites/${id}`);
    toast.success("Invite revoked");
    load();
  };
  const deactivate = async (id) => {
    try {
      await api.post(`/team/${id}/deactivate`);
      toast.success("Doctor removed from active team");
      setConfirmRemoveId(null);
      load();
    } catch (err) { toast.error(apiError(err, "Removal failed")); }
  };
  const reactivate = async (id) => {
    try {
      await api.post(`/team/${id}/reactivate`);
      toast.success("Doctor reactivated");
      load();
    } catch (err) { toast.error(apiError(err, "Reactivation failed")); }
  };
  const toggleOnlineBooking = async (id, val) => {
    try { await api.patch(`/team/${id}`, { available_for_online_booking: val }); load(); }
    catch (err) { toast.error(apiError(err, "Failed")); }
  };
  // Generic patch used by the Booking Settings drawer. Optimistically merges
  // the change into local state so toggles feel instant, then refreshes.
  const patchMember = async (id, patch) => {
    setTeam((t) => t.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    try {
      await api.patch(`/team/${id}`, patch);
      load();
    } catch (err) {
      toast.error(apiError(err, "Save failed"));
      load(); // rollback to server truth
    }
  };
  // Doctors are shown in `booking_order` ascending; nudge up/down swaps the
  // clicked row with its neighbour and persists both new positions.
  const nudgeOrder = async (idx, dir) => {
    const doctors = visible.filter((x) => x.role === "doctor");
    const to = idx + dir;
    if (to < 0 || to >= doctors.length) return;
    const a = doctors[idx], b = doctors[to];
    const aOrder = typeof a.booking_order === "number" ? a.booking_order : (idx + 1);
    const bOrder = typeof b.booking_order === "number" ? b.booking_order : (to + 1);
    try {
      await Promise.all([
        api.patch(`/team/${a.id}`, { booking_order: bOrder }),
        api.patch(`/team/${b.id}`, { booking_order: aOrder }),
      ]);
      load();
    } catch (err) { toast.error(apiError(err, "Reorder failed")); }
  };

  const visible = team
    .filter((m) => filter === "doctors" ? m.role === "doctor" :
                   filter === "staff"   ? m.role !== "doctor" : true)
    .sort((a, b) => {
      if (a.role === "doctor" && b.role === "doctor") {
        const oa = typeof a.booking_order === "number" ? a.booking_order : 999;
        const ob = typeof b.booking_order === "number" ? b.booking_order : 999;
        return oa - ob || (a.name || "").localeCompare(b.name || "");
      }
      return 0;
    });

  const doctors = visible.filter((m) => m.role === "doctor");
  const staff   = visible.filter((m) => m.role !== "doctor");

  const TEAM_TITLES = { doctors: "Doctors", staff: "Staff" };
  const title = TEAM_TITLES[filter] || "Doctors & Team";
  const description = filter === "doctors"
    ? "Manage your practising doctors — their public visibility, consultation fees, and order on the booking page."
    : filter === "staff"
      ? "Manage receptionists and admins. Invite new teammates and control roles."
      : "All the humans running your clinic — doctors, receptionists and admins.";

  const activeDoctorInSheet = doctors.find((d) => d.id === sheetDoctorId);

  return (
    <>
      <SectionHeader title={title} description={description} />

      {/* -------------------------------------------------------------- */}
      {/* Pending Invitations — surfaced above the roster so admins act on  */}
      {/* them quickly. Only shown when there is at least one pending link.  */}
      {/* -------------------------------------------------------------- */}
      {isAdmin && invites.length > 0 && (
        <section
          className="mb-5 rounded-2xl border border-[#EAC5AE] bg-[#FDF5EE] p-5 sm:p-6"
          data-testid="pending-invites"
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#B0521F]" />
                <div className="font-display text-lg text-ink">Pending invitations</div>
              </div>
              <div className="text-xs text-ink-mid mt-1">
                {invites.length} {invites.length === 1 ? "invite is" : "invites are"} waiting to be accepted.
              </div>
            </div>
          </div>
          <div className="grid gap-2">
            {invites.map((inv) => (
              <div
                key={inv.id}
                data-testid={`invite-row-${inv.id}`}
                className="flex items-center justify-between gap-3 rounded-xl bg-white border border-[#F0E1D0] px-4 py-3"
              >
                <div className="min-w-0 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full grid place-items-center text-xs font-semibold ${_avatarTint(inv.id)}`}>
                    {_initials(inv.email)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-ink truncate">{inv.email}</div>
                    <div className="text-[11px] text-ink-mid">
                      Role: <span className="font-medium">{ROLE_LABEL[inv.role] || inv.role}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => copyLink(inv.token)}
                    data-testid={`invite-copy-${inv.id}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#E8DBC7] text-ink hover:bg-linen"
                    title="Copy invite link"
                  >
                    <Copy className="w-3 h-3" /> Copy link
                  </button>
                  <button
                    onClick={() => revoke(inv.id)}
                    data-testid={`invite-revoke-${inv.id}`}
                    className="p-1.5 rounded-full text-[#B94A48] hover:bg-[#F9E5E3]"
                    title="Revoke invite"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* -------------------------------------------------------------- */}
      {/* Roster header + Invite CTA                                       */}
      {/* -------------------------------------------------------------- */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="text-xs text-ink-mid">
          {visible.length} {visible.length === 1 ? "member" : "members"}
        </div>
        {isAdmin && (
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <button data-testid="btn-invite-teammate"
                      className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-pine text-linen hover:opacity-90 transition">
                <Plus className="w-3.5 h-3.5" /> Invite teammate
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-display text-2xl">Invite teammate</DialogTitle></DialogHeader>
              <form onSubmit={invite} className="space-y-3">
                <input required type="email" placeholder="Email"
                       data-testid="invite-email"
                       className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm"
                       value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} />
                <select className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm"
                        data-testid="invite-role"
                        value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}>
                  <option value="receptionist">Receptionist</option>
                  <option value="doctor">Doctor</option>
                  <option value="admin">Admin</option>
                </select>
                <button data-testid="invite-submit" className="w-full btn-primary text-sm">Create invite link</button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* -------------------------------------------------------------- */}
      {/* Doctors — premium card grid                                       */}
      {/* -------------------------------------------------------------- */}
      {(filter === "all" || filter === "doctors") && doctors.length > 0 && (
        <div className="mb-6" data-testid="doctors-grid">
          {filter === "all" && (
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-soft mb-3">
              Doctors
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {doctors.map((m, idx) => {
              const online   = m.available_for_online_booking !== false;
              const scope    = m.consultation_fee_charge_scope || "every_visit";
              const feeText  = scope === "free"
                ? "Free consult"
                : m.consultation_fee
                  ? `₹${Number(m.consultation_fee).toLocaleString("en-IN")} · ${SCOPE_LABEL[scope]}`
                  : "No fee set";
              const orderNum = typeof m.booking_order === "number" ? m.booking_order : idx + 1;
              return (
                <article
                  key={m.id}
                  data-testid={`team-row-${m.id}`}
                  className="rounded-2xl bg-white border border-[#EFEADC] p-5 hover:border-pine/40 hover:shadow-[0_10px_30px_-20px_rgba(0,0,0,0.15)] transition"
                >
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className={`w-12 h-12 rounded-full grid place-items-center font-semibold text-sm shrink-0 ${_avatarTint(m.id)}`}>
                      {_initials(m.name)}
                    </div>

                    {/* Name + specialty + status */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-ink truncate">{m.name || "Doctor"}</div>
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                            online
                              ? "bg-[#E8F0EB] text-[#2D6A4F]"
                              : "bg-[#F1E9E9] text-[#8A2E2C]"
                          }`}
                          data-testid={`doctor-status-${m.id}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${online ? "bg-[#2D8659]" : "bg-[#B94A48]"}`} />
                          {online ? "Live" : "Hidden"}
                        </span>
                      </div>
                      <div className="text-xs text-ink-mid mt-0.5 truncate">
                        {m.specialty || "General dentistry"}
                      </div>
                      <div className="text-[11px] text-ink-soft mt-0.5 truncate">{m.email}</div>
                    </div>

                    {/* More menu */}
                    {isAdmin && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`team-more-${m.id}`}
                            className="w-8 h-8 grid place-items-center rounded-full text-ink-soft hover:bg-linen"
                            aria-label="More options"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-ink-soft">
                            {m.name || "Doctor"}
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setSheetDoctorId(m.id)}
                            data-testid={`team-menu-booking-${m.id}`}
                          >
                            <Sliders className="w-3.5 h-3.5 mr-2" /> Booking settings
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => toggleOnlineBooking(m.id, !online)}
                            data-testid={`team-menu-toggle-${m.id}`}
                          >
                            {online
                              ? <><EyeOff className="w-3.5 h-3.5 mr-2" /> Hide from booking</>
                              : <><Eye className="w-3.5 h-3.5 mr-2" /> Show on booking</>}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => copyEmail(m.email)}
                            data-testid={`team-menu-copy-${m.id}`}
                          >
                            <Mail className="w-3.5 h-3.5 mr-2" /> Copy email
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setConfirmRemoveId(m.id)}
                            data-testid={`team-menu-remove-${m.id}`}
                            className="text-[#B94A48] focus:text-[#B94A48]"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Remove from clinic
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>

                  {/* Meta row: consultation fee + reorder */}
                  <div className="mt-4 pt-4 border-t border-[#F1EBDD] flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${
                        scope === "free" ? "bg-[#E8F0EB] text-[#2D6A4F]" : "bg-linen text-ink"
                      }`}>
                        <IndianRupee className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-ink-soft">Consultation</div>
                        <div className="text-xs font-medium text-ink truncate" data-testid={`doctor-fee-summary-${m.id}`}>
                          {feeText}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => setSheetDoctorId(m.id)}
                          data-testid={`doctor-open-booking-${m.id}`}
                          className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-full border border-pine/25 text-pine hover:bg-pine/5"
                        >
                          <Sliders className="w-3 h-3" /> Booking
                        </button>
                      )}
                      {isAdmin && (
                        <div
                          className="flex items-center gap-0.5 rounded-full border border-[#E8EAE9] bg-white px-1 py-0.5"
                          data-testid={`team-order-${m.id}`}
                        >
                          <button
                            type="button"
                            disabled={idx <= 0}
                            onClick={() => nudgeOrder(idx, -1)}
                            data-testid={`team-order-up-${m.id}`}
                            className="w-6 h-6 rounded-full hover:bg-linen text-ink-soft grid place-items-center disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move up"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <span className="text-[10px] font-mono text-ink-soft w-6 text-center">
                            #{orderNum}
                          </span>
                          <button
                            type="button"
                            disabled={idx >= doctors.length - 1}
                            onClick={() => nudgeOrder(idx, +1)}
                            data-testid={`team-order-down-${m.id}`}
                            className="w-6 h-6 rounded-full hover:bg-linen text-ink-soft grid place-items-center disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move down"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}

      {/* -------------------------------------------------------------- */}
      {/* Staff — compact card list (receptionists, admins)                */}
      {/* -------------------------------------------------------------- */}
      {(filter === "all" || filter === "staff") && staff.length > 0 && (
        <div data-testid="staff-list">
          {filter === "all" && (
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-soft mb-3">
              Staff
            </div>
          )}
          <div className="rounded-2xl bg-white border border-[#EFEADC] overflow-hidden">
            {staff.map((m, i) => (
              <div
                key={m.id}
                data-testid={`team-row-${m.id}`}
                className={`flex items-center gap-4 px-5 py-4 ${
                  i > 0 ? "border-t border-[#F1EBDD]" : ""
                }`}
              >
                <div className={`w-10 h-10 rounded-full grid place-items-center font-semibold text-xs shrink-0 ${_avatarTint(m.id)}`}>
                  {_initials(m.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-ink truncate">{m.name || "Team member"}</div>
                  <div className="text-[11px] text-ink-mid truncate">{m.email}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-linen text-ink-mid">
                  {ROLE_LABEL[m.role] || m.role}
                </span>
                {isAdmin && (
                  <button
                    onClick={() => copyEmail(m.email)}
                    data-testid={`staff-copy-${m.id}`}
                    className="p-1.5 rounded-full text-ink-soft hover:bg-linen"
                    title="Copy email"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty states ------------------------------------------------- */}
      {visible.length === 0 && (
        <div
          className="rounded-2xl border border-dashed border-[#E7E1D3] p-10 text-center text-sm text-ink-mid"
          data-testid="team-empty"
        >
          {filter === "doctors"
            ? "No doctors yet. Invite one to start accepting appointments."
            : filter === "staff"
              ? "No staff yet. Invite receptionists and admins from the button above."
              : "No team members yet."}
        </div>
      )}

      {/* Inactive doctors — collapsed by default so it doesn't clutter    */}
      {/* the active roster. Admins can restore any deactivated teammate. */}
      {isAdmin && inactive.length > 0 && (filter === "all" || filter === "doctors") && (
        <section className="mb-6 mt-2" data-testid="inactive-doctors">
          <button
            type="button"
            onClick={() => setShowInactive((v) => !v)}
            className="text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-soft hover:text-ink flex items-center gap-2"
            data-testid="toggle-inactive-doctors"
          >
            <span className={`transition-transform ${showInactive ? "rotate-90" : ""}`}>▸</span>
            Removed doctors · {inactive.length}
          </button>
          {showInactive && (
            <div className="mt-3 grid gap-2">
              {inactive.map((m) => (
                <div
                  key={m.id}
                  data-testid={`inactive-row-${m.id}`}
                  className="flex items-center justify-between gap-3 rounded-xl bg-white border border-dashed border-[#E7E1D3] px-4 py-3 opacity-80"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-full grid place-items-center text-xs font-semibold ${_avatarTint(m.id)} opacity-60`}>
                      {_initials(m.name)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-ink truncate">{m.name || "Doctor"}</div>
                      <div className="text-[11px] text-ink-mid">{m.email}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => reactivate(m.id)}
                    data-testid={`reactivate-${m.id}`}
                    className="text-xs px-3 py-1.5 rounded-full border border-pine/30 text-pine hover:bg-pine/5"
                  >
                    Reactivate
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Booking settings drawer -------------------------------------- */}
      <DoctorBookingSheet
        member={activeDoctorInSheet}
        open={!!activeDoctorInSheet}
        onOpenChange={(o) => { if (!o) setSheetDoctorId(null); }}
        onPatch={patchMember}
      />

      {/* Remove-doctor confirmation dialog ---------------------------- */}
      <Dialog open={!!confirmRemoveId} onOpenChange={(o) => !o && setConfirmRemoveId(null)}>
        <DialogContent className="max-w-md" data-testid="confirm-remove-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Remove doctor from clinic?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ink-mid leading-relaxed">
            Existing appointments, prescriptions, treatments, invoices and
            historical records for this doctor will be preserved. They will
            no longer receive new bookings and won't appear on the public
            booking page.
          </p>
          <p className="text-xs text-ink-soft">
            You can reactivate them any time from Settings → Doctors →
            Removed doctors.
          </p>
          <div className="flex gap-2 justify-end mt-2">
            <button
              onClick={() => setConfirmRemoveId(null)}
              className="text-sm px-4 py-2 rounded-full border border-[#E8EAE9] hover:bg-linen"
              data-testid="confirm-remove-cancel"
            >
              Cancel
            </button>
            <button
              onClick={() => deactivate(confirmRemoveId)}
              className="text-sm px-4 py-2 rounded-full bg-[#B94A48] text-white hover:opacity-90"
              data-testid="confirm-remove-submit"
            >
              Remove doctor
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ============================================================
// Utilities
// ============================================================
function Loader() {
  return <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-ink-soft" /></div>;
}


// ============================================================
// WORKFLOW MODE — Solo / Reception / Multi-Doctor
// ============================================================
export function WorkflowModeSection() {
  const { clinic, refresh } = useClinic();
  const current = clinic?.settings?.workflow_mode || "reception";
  const explicit = clinic?.settings?.workflow_mode_explicit;
  const suggested = clinic?.settings?.workflow_mode_suggested;
  const [saving, setSaving] = useState(null);

  const save = async (mode) => {
    setSaving(mode);
    try {
      const { data } = await api.patch("/clinics/settings/workflow-mode", { mode });
      toast.success(`Workflow mode: ${MODE_META[mode].label}`);
      // Refresh cached clinic doc so every open surface picks up the change.
      const fresh = await refresh();
      primeClinicCache(fresh);
      // Silences unused-var lint on `data` — we only need the toast side-effect.
      void data;
    } catch (e) {
      toast.error(apiError(e, "Could not update workflow mode"));
    } finally {
      setSaving(null);
    }
  };

  return (
    <>
      <SectionHeader
        title="Workflow Mode"
        description="Match Viyora's day-to-day controls to how your clinic actually runs. Data is never changed — only which buttons each role sees."
      />
      <SettingCard>
        {!explicit && suggested && (
          <div
            className="mb-4 rounded-xl border border-[#EAC5AE] bg-[#FDF3ED] px-4 py-3 text-sm text-[#8A3F1E]"
            data-testid="workflow-mode-suggestion"
          >
            <b>Suggestion:</b> Based on {(clinic?.settings?.workflow_mode_suggested === "solo") ? "your single-doctor, single-chair setup" : (suggested === "multi_doctor" ? "your 3+ doctors / chairs" : "your team size")}, we recommend <b>{MODE_META[suggested].label}</b>.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="workflow-mode-grid">
          {MODES.map((m) => {
            const meta = MODE_META[m];
            const active = current === m;
            return (
              <button
                key={m}
                type="button"
                data-testid={`workflow-mode-${m}`}
                onClick={() => !active && save(m)}
                disabled={saving !== null || active}
                className={
                  "text-left rounded-2xl border p-5 transition " +
                  (active
                    ? "border-pine bg-pine/5 shadow-sm"
                    : "border-[#E8EAE9] bg-white hover:border-pine/60 hover:bg-pine/5")
                }
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-ink">{meta.label}</div>
                  {active && (
                    <span className="text-[10px] uppercase tracking-widest bg-pine text-white rounded-full px-2 py-0.5">
                      Active
                    </span>
                  )}
                  {!active && suggested === m && !explicit && (
                    <span className="text-[10px] uppercase tracking-widest bg-[#EAC5AE] text-[#8A3F1E] rounded-full px-2 py-0.5">
                      Suggested
                    </span>
                  )}
                </div>
                <p className="text-xs text-ink-soft leading-relaxed mb-3">{meta.description}</p>
                <p className="text-[11px] uppercase tracking-widest text-ink-soft">
                  {meta.idealFor}
                </p>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-ink-soft mt-6">
          Switching modes is safe and instant — active visits and appointments are unaffected.
          Every module (Calendar, Waiting Room, Chairside, Billing) will immediately expose only
          the actions relevant to the selected mode.
        </p>
      </SettingCard>
    </>
  );
}



// ============================================================
// LABORATORIES — clinic master list for Lab Cases module
// ============================================================
export function LaboratoriesSection() {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/laboratories");
      setLabs(data || []);
    } catch (e) { toast.error(apiError(e, "Failed to load labs")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (lab) => {
    try {
      await api.patch(`/laboratories/${lab.id}`, {
        name: lab.name, active: !lab.active,
      });
      toast.success(lab.active ? "Lab disabled" : "Lab enabled");
      load();
    } catch (e) { toast.error(apiError(e, "Update failed")); }
  };

  const remove = async (lab) => {
    if (!window.confirm(`Delete "${lab.name}"?`)) return;
    try {
      await api.delete(`/laboratories/${lab.id}`);
      toast.success("Lab deleted");
      load();
    } catch (e) { toast.error(apiError(e, "Delete failed")); }
  };

  return (
    <>
      <SectionHeader
        title="Laboratories"
        description="Manage the labs your clinic sends prosthetic work to. Used everywhere in the Lab Cases module."
      />
      <SettingCard>
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-ink-mid">
            {labs.length} lab{labs.length === 1 ? "" : "s"} configured
          </div>
          <button onClick={() => setCreating(true)} data-testid="btn-add-lab"
                  className="inline-flex items-center gap-2 rounded-full bg-pine text-white text-sm font-medium px-4 py-2 hover:bg-pine/90">
            + Add lab
          </button>
        </div>
        {loading ? (
          <div className="py-6 text-center text-ink-soft text-sm">Loading…</div>
        ) : labs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E7E1D3] p-10 text-center text-sm text-ink-mid">
            No labs yet. Click "Add lab" above to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="labs-table">
              <thead>
                <tr className="text-left text-ink-soft overline border-b border-[#EFEADC]">
                  <th className="pb-3 pr-4">Name</th>
                  <th className="pb-3 pr-4">Contact</th>
                  <th className="pb-3 pr-4">Phone</th>
                  <th className="pb-3 pr-4">Email</th>
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {labs.map((l) => (
                  <tr key={l.id} className="border-b border-[#F1F2F0]"
                      data-testid={`lab-row-${l.id}`}>
                    <td className="py-3 pr-4 font-medium">{l.name}</td>
                    <td className="py-3 pr-4 text-ink-mid">{l.contact_person || "—"}</td>
                    <td className="py-3 pr-4 text-ink-mid font-mono text-xs">{l.phone || "—"}</td>
                    <td className="py-3 pr-4 text-ink-mid text-xs truncate max-w-[180px]">{l.email || "—"}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-medium ${
                        l.active ? "bg-[#E8F0EB] text-[#2D6A4F]" : "bg-[#EDEDED] text-[#6B7280]"}`}>
                        {l.active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <button onClick={() => setEditing(l)} data-testid={`edit-lab-${l.id}`}
                              className="text-xs px-3 py-1 rounded-full border border-[#E8EAE9] text-ink-mid hover:border-pine hover:text-pine ml-1">Edit</button>
                      <button onClick={() => toggleActive(l)} data-testid={`toggle-lab-${l.id}`}
                              className="text-xs px-3 py-1 rounded-full border border-[#E8EAE9] text-ink-mid hover:border-pine hover:text-pine ml-1">
                        {l.active ? "Disable" : "Enable"}
                      </button>
                      <button onClick={() => remove(l)} data-testid={`del-lab-${l.id}`}
                              className="text-xs px-3 py-1 rounded-full border border-[#EDC7C6] text-[#B94A48] hover:bg-[#FCECEC] ml-1">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingCard>

      {(creating || editing) && (
        <LabDialog existing={editing} onClose={() => { setCreating(false); setEditing(null); }}
                   onSaved={() => { setCreating(false); setEditing(null); load(); }} />
      )}
    </>
  );
}

function LabDialog({ existing, onClose, onSaved }) {
  const [form, setForm] = useState(existing || {
    name: "", contact_person: "", phone: "", whatsapp: "", email: "",
    address: "", notes: "", active: true,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (existing) {
        await api.patch(`/laboratories/${existing.id}`, form);
        toast.success("Lab updated");
      } else {
        await api.post("/laboratories", form);
        toast.success("Lab added");
      }
      onSaved();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         onClick={onClose} data-testid="lab-dialog">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-[#E7E1D3]"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#EFEADC]">
          <div className="overline text-ink-soft">Laboratory</div>
          <div className="font-display text-2xl mt-1">{existing ? "Edit lab" : "Add lab"}</div>
        </div>
        <div className="p-6 space-y-3">
          <input placeholder="Lab name *" value={form.name} data-testid="lab-fld-name"
                 onChange={(e) => setForm({...form, name: e.target.value})}
                 className="input w-full" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Contact person" value={form.contact_person || ""} data-testid="lab-fld-contact"
                   onChange={(e) => setForm({...form, contact_person: e.target.value})}
                   className="input w-full" />
            <input placeholder="Phone" value={form.phone || ""} data-testid="lab-fld-phone"
                   onChange={(e) => setForm({...form, phone: e.target.value})}
                   className="input w-full" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="WhatsApp" value={form.whatsapp || ""} data-testid="lab-fld-whatsapp"
                   onChange={(e) => setForm({...form, whatsapp: e.target.value})}
                   className="input w-full" />
            <input placeholder="Email" type="email" value={form.email || ""} data-testid="lab-fld-email"
                   onChange={(e) => setForm({...form, email: e.target.value})}
                   className="input w-full" />
          </div>
          <input placeholder="Address" value={form.address || ""} data-testid="lab-fld-address"
                 onChange={(e) => setForm({...form, address: e.target.value})}
                 className="input w-full" />
          <textarea placeholder="Notes" rows={2} value={form.notes || ""} data-testid="lab-fld-notes"
                    onChange={(e) => setForm({...form, notes: e.target.value})}
                    className="input w-full resize-none" />
          <label className="inline-flex items-center gap-2 text-sm text-ink-mid">
            <input type="checkbox" checked={form.active !== false}
                   onChange={(e) => setForm({...form, active: e.target.checked})}
                   className="w-4 h-4 accent-pine" />
            Active
          </label>
        </div>
        <div className="p-6 border-t border-[#EFEADC] flex justify-end gap-3">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-full text-ink-mid hover:bg-linen">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="btn-save-lab"
                  className="text-sm rounded-full bg-pine text-white px-6 py-2 font-medium hover:bg-pine/90 disabled:opacity-40">
            {existing ? "Save changes" : "Add lab"}
          </button>
        </div>
      </div>
    </div>
  );
}

